<template>
  <div class="accounts-page">
    <div class="accounts-shell">
      <header class="page-heading">
        <div>
          <span class="eyebrow">ADMINISTRATION</span>
          <h1>Accounts &amp; Access</h1>
          <p>See who can enter Modato, where they belong, and what their role allows.</p>
        </div>
        <v-btn color="primary" prepend-icon="mdi-account-plus-outline" @click="startCreateFromHeader">
          Create Account
          <v-tooltip activator="parent" location="bottom">Show active models who still need an account.</v-tooltip>
        </v-btn>
      </header>

      <section class="summary-grid" aria-label="Account summary">
        <article class="summary-card">
          <div class="summary-icon summary-icon--blue"><v-icon>mdi-account-group-outline</v-icon></div>
          <div><span>Modato Accounts</span><strong>{{ totalAccounts }}</strong></div>
        </article>
        <article class="summary-card">
          <div class="summary-icon summary-icon--green"><v-icon>mdi-account-check-outline</v-icon></div>
          <div><span>Model Accounts</span><strong>{{ modelAccountCount }}</strong></div>
        </article>
        <article class="summary-card">
          <div class="summary-icon summary-icon--amber"><v-icon>mdi-account-clock-outline</v-icon></div>
          <div><span>Models Need Setup</span><strong>{{ modelsNeedingSetup }}</strong></div>
        </article>
        <article class="summary-card">
          <div class="summary-icon summary-icon--navy"><v-icon>mdi-shield-account-outline</v-icon></div>
          <div><span>Defined Roles</span><strong>{{ roles.length }}</strong></div>
        </article>
      </section>

      <v-card class="directory-card" elevation="0">
        <v-tabs v-model="activeTab" color="primary" class="directory-tabs">
          <v-tab value="models">Models <span class="tab-count">{{ models.length }}</span></v-tab>
          <v-tab value="team">Team Members <span class="tab-count">{{ teamMembers.length }}</span></v-tab>
          <v-tab value="roles">Roles <span class="tab-count">{{ roles.length }}</span></v-tab>
        </v-tabs>

        <div v-if="activeTab !== 'roles'" class="directory-toolbar">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Search by name or email"
            variant="outlined"
            density="compact"
            hide-details
            clearable
          />
          <v-select
            v-model="studioFilter"
            :items="studioOptions"
            label="Studio"
            variant="outlined"
            density="compact"
            hide-details
          />
          <v-select
            v-if="activeTab === 'models'"
            v-model="statusFilter"
            :items="statusOptions"
            label="Account status"
            variant="outlined"
            density="compact"
            hide-details
          />
        </div>

        <div v-if="loading" class="state-panel">
          <v-progress-circular indeterminate color="primary" size="30" />
          <span>Loading accounts and access…</span>
        </div>
        <div v-else-if="errorMessage" class="state-panel state-panel--error">
          <v-icon>mdi-alert-circle-outline</v-icon>
          <span>{{ errorMessage }}</span>
          <v-btn variant="text" color="primary" @click="loadDirectory">Try again</v-btn>
        </div>

        <div v-else-if="activeTab === 'models'" class="table-wrap">
          <table class="access-table">
            <thead>
              <tr><th>Model</th><th>Studio</th><th>Login Email</th><th>Access</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="model in filteredModels" :key="model.model_id">
                <td>
                  <div class="person-cell">
                    <span class="avatar">{{ initials(model.stage_name || model.full_name) }}</span>
                    <div><strong>{{ model.stage_name || model.full_name || 'Unnamed model' }}</strong><small>{{ model.full_name }}</small></div>
                  </div>
                </td>
                <td><span class="studio-badge">{{ studioLabel(model) }}</span></td>
                <td><span :class="{ muted: !model.login_email }">{{ model.login_email || 'Webmail not added' }}</span></td>
                <td><span class="access-label">Own model profile</span></td>
                <td>
                  <button
                    type="button"
                    :class="['status-pill', 'status-pill--button', `status-pill--${modelAccountStatus(model).key}`]"
                    @click="openModelAccount(model)"
                  >{{ modelAccountStatus(model).label }}</button>
                </td>
                <td><v-btn icon="mdi-chevron-right" variant="text" size="small" aria-label="Open account setup" @click="openModelAccount(model)" /></td>
              </tr>
              <tr v-if="filteredModels.length === 0"><td colspan="6" class="empty-row">No models match these filters.</td></tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="activeTab === 'team'" class="table-wrap">
          <table class="access-table">
            <thead>
              <tr><th>Team Member</th><th>Studio</th><th>Role</th><th>Current Access</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="member in filteredTeamMembers" :key="member.id">
                <td>
                  <div class="person-cell">
                    <span class="avatar avatar--team">{{ initials(member.email) }}</span>
                    <div><strong>{{ member.email }}</strong><small>Modato account</small></div>
                  </div>
                </td>
                <td><span class="studio-badge">{{ studioLabel(member) }}</span></td>
                <td><span class="role-pill">{{ roleLabel(member.role) }}</span></td>
                <td><span class="access-summary">{{ accessSummary(member.access) }}</span></td>
                <td><span :class="['status-pill', `status-pill--${member.account_status || 'active'}`]">{{ accountStatusLabel(member.account_status) }}</span></td>
                <td><v-btn icon="mdi-chevron-right" variant="text" size="small" disabled aria-label="Account details coming next" /></td>
              </tr>
              <tr v-if="filteredTeamMembers.length === 0"><td colspan="6" class="empty-row">No team members match these filters.</td></tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="activeTab === 'roles'" class="roles-grid">
          <article v-for="role in roles" :key="role.key" class="role-card">
            <div class="role-card__header">
              <span class="role-card__icon"><v-icon>mdi-shield-outline</v-icon></span>
              <div><h2>{{ roleLabel(role.key) }}</h2><p>{{ roleDescription(role.key) }}</p></div>
            </div>
            <div class="role-card__access">
              <span v-for="item in role.access" :key="item"><v-icon size="16">mdi-check-circle-outline</v-icon>{{ item }}</span>
            </div>
          </article>
        </div>
      </v-card>

      <div class="phase-note">
        <v-icon size="19">mdi-information-outline</v-icon>
        <span><strong>Safe first login:</strong> a Pending account becomes Active after the model signs in successfully and is routed only to their linked profile.</span>
      </div>

      <v-dialog v-model="accountDialog" max-width="590">
        <v-card v-if="selectedModel" class="account-dialog" elevation="12">
          <v-card-title class="account-dialog__title">
            <div>
              <span class="eyebrow">MODEL ACCOUNT</span>
              <h2>{{ selectedModel.stage_name || selectedModel.full_name || 'Model' }}</h2>
            </div>
            <v-btn icon="mdi-close" variant="text" size="small" aria-label="Close" :disabled="savingAccount || updatingStatus" @click="closeAccountDialog" />
          </v-card-title>

          <v-card-text class="account-dialog__body">
            <div class="account-identity">
              <div><span>Studio</span><strong>{{ studioLabel(selectedModel) }}</strong></div>
              <div><span>Login</span><strong>{{ selectedModel.login_email || 'Webmail not added' }}</strong></div>
              <div><span>Status</span><strong>{{ modelAccountStatus(selectedModel).label }}</strong></div>
            </div>

            <v-alert v-if="!selectedModel.user_id && !hasValidLoginEmail(selectedModel)" type="warning" variant="tonal" density="compact" class="dialog-alert">
              Add a valid Webmail email in Datos Modelos first. It will become this model’s Modato login.
            </v-alert>

            <template v-else>
              <v-alert v-if="!selectedModel.user_id" type="info" variant="tonal" density="compact" class="dialog-alert">
                The password is hashed before storage. The account stays Pending until the model’s first successful login, then activates automatically.
              </v-alert>
              <v-alert v-else type="info" variant="tonal" density="compact" class="dialog-alert">
                Passwords cannot be viewed after saving. You can replace this account’s password below.
              </v-alert>

              <div class="password-form">
                <div class="password-form__heading">
                  <div>
                    <h3>{{ selectedModel.user_id ? 'Set a new password' : 'Create the Modato password' }}</h3>
                    <p>Use at least 10 characters. A unique password is safest.</p>
                  </div>
                  <v-btn type="button" size="small" variant="outlined" color="primary" prepend-icon="mdi-auto-fix" @click="generatePassword">
                    Generate
                  </v-btn>
                </div>
                <v-text-field
                  v-model="password"
                  type="text"
                  :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  label="New access code"
                  variant="outlined"
                  density="comfortable"
                  autocomplete="off"
                  name="model-account-setup-code"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  :class="{ 'secret-field--masked': !showPassword }"
                  :disabled="savingAccount"
                  @click:append-inner="showPassword = !showPassword"
                />
                <v-text-field
                  v-model="passwordConfirmation"
                  type="text"
                  label="Confirm access code"
                  variant="outlined"
                  density="comfortable"
                  autocomplete="off"
                  name="model-account-setup-code-confirmation"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  :class="{ 'secret-field--masked': !showPassword }"
                  :error-messages="passwordMismatch ? ['Passwords do not match.'] : []"
                  :disabled="savingAccount"
                  @keyup.enter="savePassword"
                />
                <div v-if="generatedPassword" class="generated-password-note">
                  <v-icon size="17">mdi-content-copy</v-icon>
                  <span>Generated password is visible above. Copy it before saving; Modato will not show it again.</span>
                  <v-btn size="x-small" variant="text" color="primary" @click="copyGeneratedPassword">Copy</v-btn>
                </div>
                <v-alert v-if="accountError" type="error" variant="tonal" density="compact" class="dialog-alert">{{ accountError }}</v-alert>
                <v-btn
                  type="button"
                  color="primary"
                  block
                  :loading="savingAccount"
                  :disabled="!canSavePassword || updatingStatus"
                  @click="savePassword"
                >{{ selectedModel.user_id ? 'Save New Password' : 'Create Pending Account' }}</v-btn>
              </div>

              <div v-if="selectedModel.user_id" class="status-actions">
                <div>
                  <h3>Account status</h3>
                  <p>Disabled accounts cannot log in. Pending accounts activate automatically when the linked model signs in successfully.</p>
                </div>
                <v-btn
                  v-if="selectedModel.account_status !== 'disabled'"
                  color="error"
                  variant="outlined"
                  :loading="updatingStatus"
                  :disabled="savingAccount"
                  @click="changeAccountStatus('disabled')"
                >Disable Account</v-btn>
                <v-btn
                  v-else
                  color="primary"
                  variant="outlined"
                  :loading="updatingStatus"
                  :disabled="savingAccount"
                  @click="changeAccountStatus('pending')"
                >Continue Setup</v-btn>
              </div>
            </template>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar.visible" :color="snackbar.color" :timeout="3500">
        {{ snackbar.message }}
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import secureAxios from '@/utils/secureAxios';

const ROLE_LABELS = {
  owner: 'Owner / Master',
  admin: 'Administrator',
  user: 'Standard User',
  satellite_monitor: 'Satellite Monitor',
  hr: 'HR',
  accountant: 'Accounting',
  support: 'Support',
  model: 'Model',
};

const ROLE_DESCRIPTIONS = {
  owner: 'Cross-studio oversight and control of Accounts & Access.',
  admin: 'Full operational and administrative access.',
  user: 'Day-to-day studio operations.',
  satellite_monitor: 'Cross-studio access limited to satellite operations.',
  hr: 'Scheduling access for models and employees.',
  accountant: 'Accounting tools and view-only period context.',
  support: 'Operational support with limited financial visibility.',
  model: 'Read-only access to the model’s own information while they remain active.',
};

export default {
  name: 'AccountsAccess',
  data() {
    return {
      activeTab: 'models',
      search: '',
      studioFilter: 'all',
      statusFilter: 'all',
      loading: true,
      errorMessage: '',
      models: [],
      teamMembers: [],
      roles: [],
      accountDialog: false,
      selectedModel: null,
      password: '',
      passwordConfirmation: '',
      showPassword: false,
      generatedPassword: false,
      savingAccount: false,
      updatingStatus: false,
      accountError: '',
      snackbar: { visible: false, message: '', color: 'success' },
      statusOptions: [
        { title: 'All statuses', value: 'all' },
        { title: 'Active', value: 'active' },
        { title: 'Pending access', value: 'pending' },
        { title: 'Disabled', value: 'disabled' },
        { title: 'Needs setup', value: 'needs-setup' },
        { title: 'Missing email', value: 'missing-email' },
      ],
    };
  },
  computed: {
    totalAccounts() {
      return this.teamMembers.length + this.modelAccountCount;
    },
    modelAccountCount() {
      return this.models.filter((model) => Boolean(model.user_id)).length;
    },
    modelsNeedingSetup() {
      return this.models.filter((model) => model.model_status === 'Active' && !model.user_id).length;
    },
    studioOptions() {
      const studios = new Map();
      [...this.models, ...this.teamMembers].forEach((person) => {
        if (person.studio_id) studios.set(Number(person.studio_id), person.studio_name || `Studio ${person.studio_id}`);
      });
      return [
        { title: 'All studios', value: 'all' },
        ...[...studios.entries()].map(([value, title]) => ({ title, value })).sort((a, b) => a.title.localeCompare(b.title)),
      ];
    },
    filteredModels() {
      const needle = String(this.search || '').trim().toLowerCase();
      return this.models.filter((model) => {
        const matchesSearch = !needle || [model.stage_name, model.full_name, model.login_email, model.studio_name]
          .some((value) => String(value || '').toLowerCase().includes(needle));
        const matchesStudio = this.studioFilter === 'all' || Number(model.studio_id) === Number(this.studioFilter);
        const matchesStatus = this.statusFilter === 'all' || this.modelAccountStatus(model).key === this.statusFilter;
        return matchesSearch && matchesStudio && matchesStatus;
      });
    },
    filteredTeamMembers() {
      const needle = String(this.search || '').trim().toLowerCase();
      return this.teamMembers.filter((member) => {
        const matchesSearch = !needle || [member.email, member.role, member.studio_name]
          .some((value) => String(value || '').toLowerCase().includes(needle));
        const matchesStudio = this.studioFilter === 'all' || Number(member.studio_id) === Number(this.studioFilter);
        return matchesSearch && matchesStudio;
      });
    },
    passwordMismatch() {
      return Boolean(this.passwordConfirmation) && this.password !== this.passwordConfirmation;
    },
    canSavePassword() {
      return this.password.length >= 10
        && this.password.length <= 72
        && this.password === this.passwordConfirmation;
    },
  },
  mounted() {
    this.loadDirectory();
  },
  methods: {
    async loadDirectory() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const { data } = await secureAxios.get('/api/accounts-access/directory');
        this.models = data.models || [];
        this.teamMembers = data.teamMembers || [];
        this.roles = data.roles || [];
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Unable to load the Accounts & Access directory.';
      } finally {
        this.loading = false;
      }
    },
    initials(value) {
      const parts = String(value || '?').replace('@', ' ').split(/[\s._-]+/).filter(Boolean);
      return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || '?';
    },
    studioLabel(person) {
      const knownCodes = { 1: 'CC', 2: 'CJ', 3: 'BOG', 4: 'PEI' };
      return knownCodes[Number(person.studio_id)] || person.studio_name || '—';
    },
    modelAccountStatus(model) {
      if (model.user_id && model.account_status === 'disabled') return { key: 'disabled', label: 'Disabled' };
      if (model.user_id && model.account_status === 'pending') return { key: 'pending', label: 'Pending access' };
      if (model.user_id) return { key: 'active', label: 'Active' };
      if (!String(model.login_email || '').trim()) return { key: 'missing-email', label: 'Missing email' };
      return { key: 'needs-setup', label: 'Needs setup' };
    },
    accountStatusLabel(status) {
      const labels = { active: 'Active', pending: 'Pending access', disabled: 'Disabled' };
      return labels[status] || 'Active';
    },
    hasValidLoginEmail(model) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(model?.login_email || '').trim());
    },
    startCreateFromHeader() {
      this.activeTab = 'models';
      this.statusFilter = 'needs-setup';
      this.search = '';
      this.snackbar = {
        visible: true,
        color: 'primary',
        message: 'Choose a model by clicking Needs setup or the arrow.',
      };
    },
    openModelAccount(model) {
      this.selectedModel = { ...model };
      this.password = '';
      this.passwordConfirmation = '';
      this.showPassword = false;
      this.generatedPassword = false;
      this.accountError = '';
      this.accountDialog = true;
    },
    closeAccountDialog() {
      if (this.savingAccount || this.updatingStatus) return;
      this.accountDialog = false;
      this.selectedModel = null;
      this.password = '';
      this.passwordConfirmation = '';
      this.accountError = '';
    },
    generatePassword() {
      const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
      const values = new Uint32Array(18);
      window.crypto.getRandomValues(values);
      const generated = Array.from(values, (value) => alphabet[value % alphabet.length]).join('');
      this.password = generated;
      this.passwordConfirmation = generated;
      this.showPassword = true;
      this.generatedPassword = true;
      this.accountError = '';
    },
    async copyGeneratedPassword() {
      try {
        await navigator.clipboard.writeText(this.password);
        this.snackbar = { visible: true, color: 'success', message: 'Password copied.' };
      } catch (error) {
        this.snackbar = { visible: true, color: 'error', message: 'Could not copy the password. Select and copy it manually.' };
      }
    },
    async savePassword() {
      if (!this.selectedModel || !this.canSavePassword) return;
      this.savingAccount = true;
      this.accountError = '';
      const isNewAccount = !this.selectedModel.user_id;
      try {
        const url = isNewAccount
          ? `/api/accounts-access/models/${this.selectedModel.model_id}/account`
          : `/api/accounts-access/models/${this.selectedModel.model_id}/password`;
        const response = isNewAccount
          ? await secureAxios.post(url, { password: this.password })
          : await secureAxios.patch(url, { password: this.password });
        await this.loadDirectory();
        this.savingAccount = false;
        this.closeAccountDialog();
        this.snackbar = { visible: true, color: 'success', message: response.data.message || 'Account saved.' };
      } catch (error) {
        this.accountError = error.response?.data?.message || 'Unable to save the account.';
      } finally {
        this.savingAccount = false;
      }
    },
    async changeAccountStatus(status) {
      if (!this.selectedModel?.user_id) return;
      this.updatingStatus = true;
      this.accountError = '';
      try {
        const { data } = await secureAxios.patch(
          `/api/accounts-access/models/${this.selectedModel.model_id}/status`,
          { status }
        );
        this.selectedModel.account_status = status;
        await this.loadDirectory();
        this.snackbar = { visible: true, color: 'success', message: data.message || 'Account status updated.' };
      } catch (error) {
        this.accountError = error.response?.data?.message || 'Unable to update the account status.';
      } finally {
        this.updatingStatus = false;
      }
    },
    roleLabel(role) {
      return ROLE_LABELS[role] || String(role || 'Unknown').replace(/_/g, ' ');
    },
    roleDescription(role) {
      return ROLE_DESCRIPTIONS[role] || 'Existing Modato access role.';
    },
    accessSummary(access) {
      if (!Array.isArray(access) || access.length === 0) return 'No access summary';
      return access.slice(0, 2).join(' · ');
    },
  },
};
</script>

<style scoped>
.accounts-page { min-height: 100%; background: #f5f8fc; color: #203247; }
.accounts-shell { max-width: 1500px; margin: 0 auto; padding: 24px 28px 52px; }
.page-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; margin-bottom: 20px; }
.page-heading h1 { margin: 2px 0 3px; color: #0a2647; font-size: 30px; line-height: 1.15; }
.page-heading p { margin: 0; color: #64768a; font-size: 14px; }
.eyebrow { color: #2c74b3; font-size: 11px; font-weight: 900; letter-spacing: .14em; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 13px; margin-bottom: 16px; }
.summary-card { display: flex; align-items: center; gap: 13px; min-height: 88px; padding: 14px 17px; border: 1px solid #dce4ec; border-radius: 8px; background: #fff; }
.summary-card span { display: block; color: #66788a; font-size: 12px; font-weight: 700; }
.summary-card strong { display: block; margin-top: 2px; color: #0a2647; font-size: 26px; }
.summary-icon { display: grid; width: 44px; height: 44px; flex: 0 0 44px; place-items: center; border-radius: 50%; }
.summary-icon--blue { background: #e6f1fa; color: #205295; }
.summary-icon--green { background: #e7f5ec; color: #27824b; }
.summary-icon--amber { background: #fff2dd; color: #b87511; }
.summary-icon--navy { background: #e8edf4; color: #0a2647; }
.directory-card { overflow: hidden; border: 1px solid #d8e2eb; border-radius: 9px; background: #fff; }
.directory-tabs { border-bottom: 1px solid #dfe7ef; padding: 0 8px; }
.tab-count { min-width: 22px; margin-left: 7px; padding: 2px 6px; border-radius: 10px; background: #edf3f8; color: #526a80; font-size: 10px; font-weight: 800; }
.directory-toolbar { display: grid; grid-template-columns: minmax(280px, 1fr) 210px 210px; gap: 10px; padding: 14px 16px; border-bottom: 1px solid #e2e9ef; background: #fbfcfe; }
.table-wrap { overflow-x: auto; }
.access-table { width: 100%; border-collapse: collapse; }
.access-table th { padding: 10px 14px; background: #f5f8fb; color: #597084; font-size: 10px; font-weight: 900; letter-spacing: .07em; text-align: left; text-transform: uppercase; white-space: nowrap; }
.access-table td { min-height: 58px; padding: 10px 14px; border-top: 1px solid #e4eaf0; color: #2b4053; font-size: 13px; vertical-align: middle; }
.access-table tbody tr:hover { background: #f8fbfe; }
.person-cell { display: flex; align-items: center; gap: 10px; min-width: 210px; }
.person-cell strong, .person-cell small, .cell-subtitle { display: block; }
.person-cell strong { color: #17314a; font-size: 13px; }
.person-cell small, .cell-subtitle { margin-top: 2px; color: #77899a; font-size: 10px; }
.avatar { display: grid; width: 34px; height: 34px; flex: 0 0 34px; place-items: center; border-radius: 50%; background: #dceefa; color: #1f6696; font-size: 11px; font-weight: 900; }
.avatar--team { background: #e9edf3; color: #425a70; }
.studio-badge { display: inline-flex; min-width: 42px; justify-content: center; padding: 4px 8px; border-radius: 5px; background: #edf3f8; color: #274b69; font-size: 11px; font-weight: 900; }
.muted { color: #9a6b22; font-style: italic; }
.access-label, .access-summary { color: #405d75; font-size: 12px; }
.role-pill, .status-pill { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 12px; font-size: 11px; font-weight: 800; white-space: nowrap; }
.role-pill { background: #e6f1fa; color: #205d8c; }
.status-pill--button { border: 0; cursor: pointer; font-family: inherit; }
.status-pill--button:hover { box-shadow: 0 0 0 2px rgba(44, 116, 179, .16); }
.status-pill--active { background: #e4f5e9; color: #247b43; }
.status-pill--pending { background: #e8eefb; color: #365fa5; }
.status-pill--disabled { background: #edf0f3; color: #677785; }
.status-pill--needs-setup { background: #fff0d7; color: #98610a; }
.status-pill--missing-email { background: #fde8e8; color: #b52d35; }
.empty-row { padding: 30px !important; color: #7a8c9c !important; text-align: center; }
.state-panel { display: flex; min-height: 250px; align-items: center; justify-content: center; gap: 12px; color: #63788d; }
.state-panel--error { color: #b63840; }
.roles-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; padding: 16px; background: #f7f9fc; }
.role-card { padding: 16px; border: 1px solid #dce4ec; border-radius: 8px; background: #fff; }
.role-card__header { display: flex; gap: 11px; }
.role-card__icon { display: grid; width: 38px; height: 38px; flex: 0 0 38px; place-items: center; border-radius: 7px; background: #e6f1fa; color: #205295; }
.role-card h2 { margin: 0; color: #17314a; font-size: 15px; }
.role-card p { margin: 3px 0 0; color: #6f8192; font-size: 11px; }
.role-card__access { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 14px; }
.role-card__access span { display: inline-flex; align-items: center; gap: 4px; padding: 5px 8px; border-radius: 5px; background: #f1f5f8; color: #496176; font-size: 10px; font-weight: 700; }
.role-card__access .v-icon { color: #2d8050; }
.phase-note { display: flex; align-items: center; gap: 9px; margin-top: 13px; padding: 11px 13px; border: 1px solid #cfe0ef; border-radius: 7px; background: #edf6fd; color: #3c607e; font-size: 12px; }
.phase-note .v-icon { color: #2c74b3; }
.account-dialog { overflow: hidden; border-radius: 10px !important; }
.account-dialog__title { display: flex; align-items: flex-start; justify-content: space-between; padding: 20px 22px 14px !important; border-bottom: 1px solid #e1e8ef; }
.account-dialog__title h2 { margin: 2px 0 0; color: #0a2647; font-size: 22px; }
.account-dialog__body { padding: 20px 22px 24px !important; }
.account-identity { display: grid; grid-template-columns: 80px minmax(0, 1fr) 120px; gap: 10px; margin-bottom: 16px; padding: 12px 14px; border: 1px solid #dce5ed; border-radius: 8px; background: #f7f9fc; }
.account-identity span, .account-identity strong { display: block; }
.account-identity span { margin-bottom: 2px; color: #738598; font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
.account-identity strong { overflow: hidden; color: #213c54; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.dialog-alert { margin-bottom: 16px; font-size: 12px; }
.password-form { padding-top: 2px; }
.secret-field--masked :deep(input) { -webkit-text-security: disc; }
.password-form__heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 13px; }
.password-form h3, .status-actions h3 { margin: 0; color: #17314a; font-size: 14px; }
.password-form p, .status-actions p { margin: 3px 0 0; color: #708294; font-size: 11px; }
.generated-password-note { display: flex; align-items: center; gap: 7px; margin: -6px 0 15px; padding: 8px 10px; border-radius: 6px; background: #eef6fd; color: #426681; font-size: 11px; }
.generated-password-note span { flex: 1; }
.status-actions { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-top: 18px; padding-top: 17px; border-top: 1px solid #e1e8ef; }
.status-actions > div { flex: 1; }
@media (max-width: 1000px) {
  .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .directory-toolbar { grid-template-columns: 1fr 180px; }
  .directory-toolbar > :last-child { grid-column: 1 / -1; }
}
@media (max-width: 700px) {
  .accounts-shell { padding: 16px 12px 40px; }
  .page-heading { align-items: flex-start; flex-direction: column; }
  .summary-grid, .roles-grid { grid-template-columns: 1fr; }
  .directory-toolbar { grid-template-columns: 1fr; }
  .directory-toolbar > :last-child { grid-column: auto; }
  .account-identity { grid-template-columns: 1fr; }
  .status-actions { align-items: stretch; flex-direction: column; }
}
</style>
